import { revalidateLogic } from '@tanstack/react-form'
import * as z from 'zod'

import { useAppForm } from '@ui/components/forms'
import { Button } from '@ui/components/shadcn/ui/button'
import { FieldGroup } from '@ui/components/shadcn/ui/field'

import {
  useAuthAnalytics,
  useAuthScreenView,
} from '@web-app/components/auth/analytics'
import { signInWithEmail } from '@web-app/lib/auth/client'
import {
  GENERIC_ERROR,
  transportFailureCopy,
} from '@web-app/lib/errors/transportFailure'
import { validateEmail } from '@web-app/utils/validation'

const schema = z.object({
  email: z.string().trim().refine(validateEmail, {
    message: 'Enter a valid email address',
  }),
  password: z.string().min(1, 'Enter your password'),
})

interface Props {
  initialEmail?: string
  redirect?: string
  // The route answers where the signed-in user goes next.
  onSuccess: (destination: string) => void | Promise<void>
  onForgotPassword: (args: { email: string }) => void
  onUsePhone: () => void
}

export function EmailLoginForm({
  initialEmail = '',
  redirect,
  onSuccess,
  onForgotPassword,
  onUsePhone,
}: Props) {
  const { trackTap } = useAuthAnalytics()
  useAuthScreenView('loginPassword')

  const form = useAppForm({
    defaultValues: { email: initialEmail, password: '' },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: schema,
      onSubmitAsync: async ({ value }) => {
        trackTap('loginPasswordNext')
        const outcome = await signInWithEmail({
          email: value.email,
          password: value.password,
          redirect,
        })
        switch (outcome.kind) {
          case 'signed-in':
            await onSuccess(outcome.destination)
            return null
          // The 401 is deliberately vague server-side; keep it that way.
          case 'rejected':
            return { form: 'Invalid email or password' }
          case 'unreachable':
            return { form: transportFailureCopy() }
          case 'failed':
            return { form: GENERIC_ERROR }
        }
      },
    },
  })

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <FieldGroup className="gap-2">
        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label="Email"
              type="email"
              autoComplete="email"
              inputMode="email"
            />
          )}
        </form.AppField>
        <form.AppField name="password">
          {(field) => (
            <field.PasswordField
              label="Password"
              autoComplete="current-password"
            />
          )}
        </form.AppField>
      </FieldGroup>
      <form.AppForm>
        <div className="mt-2 flex flex-col gap-2">
          <form.SubmitButton>Sign in</form.SubmitButton>
          <form.FormError />
        </div>
      </form.AppForm>
      <div className="flex flex-col items-center gap-2 text-sm">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm"
          onClick={() => {
            trackTap('loginForgotPassword')
            onForgotPassword({ email: form.getFieldValue('email') })
          }}
        >
          Forgot password?
        </Button>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-sm"
          onClick={onUsePhone}
        >
          Sign in with your phone instead
        </Button>
      </div>
    </form>
  )
}
